import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedActions from '../components/FixedActions';
import { projectData } from '../data/projectData';
import { Filter, Calendar, MapPin, Eye, Ruler } from 'lucide-react';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedService, setSelectedService] = useState('all');

  // Transform projectData into flat array
  const allProjects = useMemo(() => {
    const projects = [];
    
    // Sports projects with real images get priority
    const sportsServicesWithRealImages = [
      'pickleball-courts',
      'football-turf',
      'tennis-courts',
      'padel-courts'
    ];
    
    // Construction projects
    const constructionServices = [
      'residential',
      'commercial',
      'warehouse',
      'court-structures',
      'interiors'
    ];
    
    // Add priority sports projects first
    sportsServicesWithRealImages.forEach(service => {
      if (projectData[service]) {
        projectData[service].forEach((project, index) => {
          projects.push({
            ...project,
            category: 'sports',
            service: service,
            serviceName: service.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            priority: 1
          });
        });
      }
    });
    
    // Add other sports projects
    Object.keys(projectData).forEach(service => {
      if (!sportsServicesWithRealImages.includes(service) && !constructionServices.includes(service)) {
        if (projectData[service]) {
          projectData[service].forEach((project, index) => {
            projects.push({
              ...project,
              category: 'sports',
              service: service,
              serviceName: service.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
              priority: 2
            });
          });
        }
      }
    });
    
    // Add construction projects
    constructionServices.forEach(service => {
      if (projectData[service]) {
        projectData[service].forEach((project, index) => {
          projects.push({
            ...project,
            category: 'construction',
            service: service,
            serviceName: service.charAt(0).toUpperCase() + service.slice(1),
            priority: 3
          });
        });
      }
    });
    
    return projects.sort((a, b) => a.priority - b.priority);
  }, []);

  // Get unique services based on category
  const services = useMemo(() => {
    if (selectedCategory === 'all') return [];
    return [...new Set(
      allProjects
        .filter(p => p.category === selectedCategory)
        .map(p => ({ value: p.service, label: p.serviceName }))
        .map(s => JSON.stringify(s))
    )].map(s => JSON.parse(s));
  }, [selectedCategory, allProjects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return allProjects.filter(project => {
      if (selectedCategory !== 'all' && project.category !== selectedCategory) return false;
      if (selectedService !== 'all' && project.service !== selectedService) return false;
      return true;
    });
  }, [selectedCategory, selectedService, allProjects]);

  return (
    <div style={{ background: '#FAFBFC', minHeight: '100vh' }}>
      <Header />
      <FixedActions />
      
      {/* Hero Section */}
      <section style={{
        paddingTop: '140px',
        paddingBottom: '60px',
        background: 'linear-gradient(135deg, #1A1B1E 0%, #2D2E32 100%)',
        position: 'relative'
      }}>
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: 'white',
              marginBottom: '20px',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Our Projects Portfolio
            </h1>
            <p style={{
              fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.6,
              fontFamily: "'Inter', sans-serif"
            }}>
              Explore our completed sports courts and construction projects across India
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ padding: '40px 0', background: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', position: 'sticky', top: '80px', zIndex: 100 }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={20} style={{ color: '#FFD700' }} />
              <span style={{ fontWeight: 600, fontSize: '16px', color: '#1A1B1E' }}>Filter:</span>
            </div>
            
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedService('all');
              }}
              className="filter-dropdown"
              style={{
                padding: '14px 24px',
                paddingRight: '48px',
                borderRadius: '12px',
                border: '2px solid #E2E8F0',
                background: 'white',
                fontSize: '15px',
                fontWeight: 600,
                minWidth: '200px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontFamily: "'Inter', sans-serif",
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23FFD700' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 16px center',
                color: '#1A1B1E',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <option value="all">All Projects</option>
              <option value="sports">Sports Courts</option>
              <option value="construction">Construction</option>
            </select>

            {selectedCategory !== 'all' && services.length > 0 && (
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="filter-dropdown"
                style={{
                  padding: '14px 24px',
                  paddingRight: '48px',
                  borderRadius: '12px',
                  border: '2px solid #E2E8F0',
                  background: 'white',
                  fontSize: '15px',
                  fontWeight: 600,
                  minWidth: '220px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontFamily: "'Inter', sans-serif",
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23FFD700' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 16px center',
                  color: '#1A1B1E',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <option value="all">All {selectedCategory === 'sports' ? 'Sports' : 'Construction'}</option>
                {services.map(service => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))}
              </select>
            )}

            <div style={{ marginLeft: 'auto', fontSize: '15px', color: '#718096', fontWeight: 500 }}>
              Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section style={{ padding: '60px 0' }}>
        <div className="container">
          {filteredProjects.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
              gap: '32px'
            }}>
              {filteredProjects.map((project, index) => (
                <article
                  key={`${project.service}-${project.id}`}
                  style={{
                    background: 'white',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(255,215,0,0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  }}
                >
                  {/* Project Image */}
                  <div style={{ position: 'relative', height: '260px', overflow: 'hidden' }}>
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: project.category === 'sports' ? 'linear-gradient(135deg, #FFD700, #FFA500)' : 'linear-gradient(135deg, #4A5568, #2D3748)',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: 600
                    }}>
                      {project.category === 'sports' ? 'Sports' : 'Construction'}
                    </div>
                  </div>

                  {/* Project Details */}
                  <div style={{ padding: '24px' }}>
                    <div style={{
                      display: 'inline-block',
                      background: '#F7FAFC',
                      color: '#4A5568',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      marginBottom: '12px'
                    }}>
                      {project.serviceName}
                    </div>

                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: 600,
                      marginBottom: '12px',
                      color: '#1A1B1E',
                      lineHeight: 1.4,
                      fontFamily: "'Poppins', sans-serif"
                    }}>
                      {project.title}
                    </h3>

                    <p style={{
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#718096',
                      marginBottom: '16px',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      {project.description}
                    </p>

                    {/* Project Info */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      marginBottom: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid #E2E8F0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096' }}>
                        <MapPin size={14} />
                        <span>{project.location}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096' }}>
                        <Calendar size={14} />
                        <span>{project.completionDate}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096' }}>
                        <Ruler size={14} />
                        <span>{project.builtArea}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#718096' }}>
                        <Eye size={14} />
                        <span>{project.images.length} Photos</span>
                      </div>
                    </div>

                    {/* View Details Link */}
                    <Link
                      to={`/${project.category}/${project.service}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '12px',
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                        color: 'white',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      View Full Project <Eye size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '80px 20px',
              background: 'white',
              borderRadius: '20px'
            }}>
              <Filter size={48} style={{ color: '#E2E8F0', marginBottom: '16px' }} />
              <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px', color: '#1A1B1E' }}>
                No Projects Found
              </h3>
              <p style={{ fontSize: '16px', color: '#718096' }}>
                Try adjusting your filters to see more results
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .filter-dropdown:hover {
          border-color: #FFD700;
          box-shadow: 0 4px 16px rgba(255, 215, 0, 0.2);
        }
        
        .filter-dropdown:focus {
          outline: none;
          border-color: #FFD700;
          box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
        }
        
        @media (max-width: 1024px) {
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
        
        @media (max-width: 768px) {
          section {
            padding: 60px 20px !important;
          }
          
          section:first-of-type {
            padding-top: 100px !important;
            padding-bottom: 50px !important;
          }
          
          section:first-of-type h1 {
            font-size: 2rem !important;
          }
          
          section:first-of-type p {
            font-size: 1rem !important;
          }
          
          /* Filters section */
          section:nth-of-type(2) {
            padding: 20px !important;
            top: 60px !important;
          }
          
          section:nth-of-type(2) > div > div {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          
          .filter-dropdown {
            width: 100% !important;
            min-width: auto !important;
          }
          
          /* Projects grid */
          section:nth-of-type(3) > div > div {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          
          .container {
            padding: 0 20px !important;
          }
        }
        
        @media (max-width: 480px) {
          section:first-of-type h1 {
            font-size: 1.75rem !important;
          }
          
          section:nth-of-type(2) {
            padding: 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Projects;
